import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import {
  Phone,
  MapPin,
  Clock,
  Flame,
  Feather,
  Leaf,
  ArrowUpRight,
  ChevronDown,
  ShoppingBag,
  Bike,
  Utensils,
  QrCode,
  Instagram,
  Facebook,
  MessageCircle,
  Star,
} from "lucide-react";
import { BUSINESS, MENU, QUALITY, ORDER_OPTIONS } from "@/data/rosto";
import { useOrderModal } from "@/components/OrderModal";

const ICONS = { Flame, Feather, Leaf, MapPin };

/* Brand icons not in lucide */
const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.321 5.562a5.123 5.123 0 01-.443-.258 6.228 6.228 0 01-1.137-.966c-.849-.971-1.165-1.956-1.282-2.645h.004C16.363.85 16.424.025 16.433 0H13.04v13.12c0 .176 0 .35-.008.522 0 .022-.002.042-.003.065l-.002.03v.007a2.884 2.884 0 01-1.452 2.287 2.834 2.834 0 01-1.402.368c-1.564 0-2.832-1.276-2.832-2.85 0-1.575 1.268-2.852 2.832-2.852.296 0 .59.047.87.139l.004-3.45a6.305 6.305 0 00-.876-.061C6.683 7.325 3.9 10.125 3.9 13.552c0 2.102 1.046 3.959 2.646 5.083a6.184 6.184 0 003.627 1.165c3.49 0 6.275-2.8 6.275-6.227V6.707a9.61 9.61 0 002.393 1.58c.572.264 1.177.461 1.8.586V4.94a5.202 5.202 0 01-1.32-.378z" />
  </svg>
);
const WhatsAppIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.967-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.371-.272.298-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const SectionLabel = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="font-label text-[11px] sm:text-xs text-[#C86C3F] flex items-center gap-3"
  >
    <span className="inline-block w-8 h-px bg-[#C86C3F]" />
    {children}
  </div>
);

/* ---------- NAV ---------- */
const Nav = () => {
  const { open } = useOrderModal();
  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0F0F0F]/75 border-b border-[#362A26]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/rostos-logo.png"
            alt="Rosto's"
            className="h-10 w-10 object-contain"
          />
          <span className="font-display text-xl text-[#D9531E] tracking-tight leading-none uppercase">
            ROSTO'S
          </span>
          <span className="hidden sm:inline-block font-label text-[10px] text-[#B2A69E] border-l border-[#362A26] pl-3 ml-1">
            Garbatella
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 font-label text-[11px] text-[#B2A69E]">
          <a href="#menu" className="hover:text-[#F5F1E6] transition-colors">
            Menu
          </a>
          <a href="#qualita" className="hover:text-[#F5F1E6] transition-colors">
            Qualità
          </a>
          <a href="#ordina" className="hover:text-[#F5F1E6] transition-colors">
            Ordina
          </a>
          <a href="#visita" className="hover:text-[#F5F1E6] transition-colors">
            Visita
          </a>
        </nav>

        <button
          type="button"
          onClick={open}
          data-testid="nav-cta-order"
          className="btn-copper px-4 sm:px-5 py-2.5 rounded-full font-label text-[11px]"
        >
          Ordina a domicilio
        </button>
      </div>
    </header>
  );
};

/* ---------- HERO ---------- */
const Hero = () => {
  const { open } = useOrderModal();
  return (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[100svh] flex items-end overflow-hidden"
  >
    <img
      src="/pollo-intero.jpg"
      alt="Miglior pollo arrosto Campese a Roma Garbatella — Rosto's"
      className="absolute inset-0 w-full h-full object-cover"
      loading="eager"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/85 via-[#0F0F0F]/55 to-[#0F0F0F]" />
    <div className="absolute inset-0 grain" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-12 sm:pb-20">
      <div className="max-w-3xl fadeup">
        <SectionLabel testid="hero-eyebrow">
          Pollo arrosto Campese · Garbatella · Roma
        </SectionLabel>
        <h1
          data-testid="hero-title"
          className="font-display mt-6 text-4xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F5F1E6]"
        >
          Pollo arrosto{" "}
          <span className="text-[#E68153]">Campese</span> a Roma Garbatella.
        </h1>
        <p
          data-testid="hero-sub"
          className="mt-6 text-base sm:text-lg text-[#B2A69E] max-w-xl leading-relaxed"
        >
          Pollo arrosto succoso, pelle croccante, patate al forno e sapori
          veri da rosticceria moderna. Da ROSTO'S, a Garbatella.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={open}
            data-testid="hero-cta-order"
            className="btn-copper px-6 py-4 rounded-full font-label text-xs flex items-center gap-2"
          >
            <Bike className="w-4 h-4" />
            Ordina a domicilio
          </button>
          <a
            href="/menu"
            data-testid="hero-cta-menu"
            className="btn-outline-copper px-6 py-4 rounded-full font-label text-xs flex items-center gap-2"
          >
            <Utensils className="w-4 h-4" />
            Scopri il menu
          </a>
          <a
            href={`tel:${BUSINESS.phoneLink}`}
            data-testid="hero-cta-phone"
            className="btn-outline-copper px-6 py-4 rounded-full font-label text-xs flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Chiama
          </a>
        </div>

        <div className="mt-12 flex items-center gap-6 text-[#B2A69E] text-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#D9531E] ember-pulse" />
            <span>Aperto adesso</span>
          </div>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">{BUSINESS.hoursShort}</span>
        </div>
      </div>
    </div>

    <a
      href="#menu"
      aria-label="Scorri al menu"
      className="absolute bottom-6 right-6 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full border border-[#362A26] text-[#B2A69E] hover:text-[#F5F1E6] hover:border-[#C86C3F] transition-colors"
      data-testid="hero-scroll-cue"
    >
      <ChevronDown className="w-5 h-5" />
    </a>
  </section>
  );
};

/* ---------- MARQUEE STRIP ---------- */
const Strip = () => (
  <div
    className="border-y border-[#362A26] bg-[#0F0F0F] py-5 overflow-hidden"
    data-testid="brand-strip"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 font-label text-[10px] sm:text-[11px] text-[#B2A69E]">
      <span>Pollo Campese italiano</span>
      <span className="text-[#D9531E]">●</span>
      <span>Marinatura 48h</span>
      <span className="text-[#D9531E]">●</span>
      <span>Cottura lenta al forno</span>
      <span className="text-[#D9531E]">●</span>
      <span>Garbatella, Roma</span>
      <span className="text-[#D9531E]">●</span>
      <span>Asporto e delivery</span>
    </div>
  </div>
);

/* ---------- SEO / ABOUT (text-heavy, local SEO) ---------- */
const AboutSeo = () => (
  <section
    data-testid="about-seo-section"
    className="relative py-20 sm:py-28 bg-[#0F0F0F] border-b border-[#362A26]"
  >
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionLabel testid="about-eyebrow">Chi siamo</SectionLabel>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F1E6] mt-5 leading-[1.1]">
        Cerchi un buon pollo arrosto a Roma? Vienici a trovare a Garbatella.
      </h2>
      <div className="mt-8 space-y-5 text-[#B2A69E] text-base sm:text-lg leading-relaxed">
        <p>
          <strong className="text-[#F5F1E6]">ROSTO'S</strong> è la rosticceria
          di <strong className="text-[#F5F1E6]">Garbatella</strong> che ha
          trasformato il pollo arrosto in un piccolo rituale quotidiano. Nel
          cuore di Roma, a pochi passi dalla Basilica di San Paolo, accendiamo
          ogni mattina lo spiedo e selezioniamo solo{" "}
          <strong className="text-[#F5F1E6]">pollo Campese italiano</strong>:
          una razza rustica, allevata all'aperto, con un tempo di crescita
          lento che restituisce una carne soda, saporita e dalla pelle
          naturalmente dorata.
        </p>
        <p>
          La nostra ricetta è semplice e onesta: pollo, marinatura
          mediterranea con olio EVO, agrumi ed erbe, cottura lenta al forno
          per ottenere una pelle croccante e una polpa che resta succosa fino
          all'ultimo morso. Per chi cerca un tocco in più, prepariamo anche
          il <strong className="text-[#F5F1E6]">pollo allo spiedo</strong>{" "}
          classico e le nostre{" "}
          <strong className="text-[#F5F1E6]">alette croccanti</strong>,
          perfette da accompagnare con le nostre{" "}
          <strong className="text-[#F5F1E6]">patate al forno</strong>{" "}
          tagliate a spicchi e cotte nel grasso del pollo.
        </p>
        <p>
          Da ROSTO'S a Garbatella trovi tutto quello che serve per un pranzo o
          una cena di carattere:{" "}
          <strong className="text-[#F5F1E6]">
            pollo arrosto Campese intero
          </strong>
          , mezzo pollo, un quarto, alette croccanti, contorni di patate al
          forno regular o large, e combo pensate per pranzi veloci o serate
          in famiglia. Tutto è preparato fresco, pronto in pochi minuti.
        </p>
        <p>
          Siamo una rosticceria moderna ma con il cuore di quartiere.
          Lavoriamo per chi vive a Garbatella, Ostiense, Testaccio e San
          Paolo, ma anche per chi arriva da tutta Roma alla ricerca del
          miglior pollo arrosto della città. Per questo abbiamo reso semplice
          ordinare: puoi venire a prendere il tuo{" "}
          <strong className="text-[#F5F1E6]">pollo da asporto</strong> in
          negozio, oppure ricevere la consegna a casa tramite Glovo, Just Eat
          o Deliveroo, in tutta Roma sud.
        </p>
        <p>
          Cerchi <em>pollo arrosto Roma</em>, <em>pollo Campese Garbatella</em>,{" "}
          <em>rosticceria con consegna a domicilio</em> o un buon{" "}
          <em>pollo allo spiedo</em>? Sei nel posto giusto. ROSTO'S —
          Garbatella, Roma. Aperto tutti i giorni dalle 12:00 alle 21:45.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <a
          href="/menu"
          data-testid="about-cta-menu"
          className="btn-outline-copper px-6 py-3.5 rounded-full font-label text-[11px] flex items-center gap-2"
        >
          Guarda il menu
        </a>
        <a
          href={BUSINESS.links.mapsLink}
          target="_blank"
          rel="noreferrer"
          data-testid="about-cta-maps"
          className="btn-outline-copper px-6 py-3.5 rounded-full font-label text-[11px] flex items-center gap-2"
        >
          Indicazioni su Google Maps
        </a>
      </div>
    </div>
  </section>
);

/* ---------- MENU ---------- */
const MenuSection = () => {
  const { open } = useOrderModal();
  return (
  <section
    id="menu"
    data-testid="menu-section"
    className="relative py-24 sm:py-32 smoke-bg"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
        <div>
          <SectionLabel testid="menu-eyebrow">Menu ROSTO'S</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F1E6] mt-5 max-w-2xl leading-[1.05]">
            Combo, alette croccanti e nuggets di pollo.
          </h2>
        </div>
        <p className="text-[#B2A69E] max-w-sm text-base sm:text-lg">
          Il meglio della nostra rosticceria a Garbatella, pronto per
          l'asporto o per la consegna a domicilio a Roma.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {MENU.map((m, i) => (
          <article
            key={m.id}
            data-testid={`menu-card-${m.id}`}
            className="card-rosto rounded-2xl overflow-hidden flex flex-col"
          >
            {m.image && (
              <div className="relative w-full overflow-hidden h-56">
                <img
                  src={m.image}
                  alt={m.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
                {m.featured && (
                  <span className="absolute top-4 left-4 font-label text-[10px] px-3 py-1.5 rounded-full bg-[#D9531E] text-[#0F0F0F]">
                    Più scelto
                  </span>
                )}
              </div>
            )}

            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="font-label text-[10px] text-[#C86C3F]">
                    {m.eyebrow}
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#F5F1E6] mt-1">
                    {m.title}
                  </h3>
                </div>
                {m.sizes ? (
                  <div
                    className="flex flex-col items-end gap-0.5 whitespace-nowrap"
                    data-testid={`menu-price-${m.id}`}
                  >
                    {m.sizes.map((s) => (
                      <div
                        key={s.label}
                        className="flex items-baseline gap-2 leading-none"
                      >
                        <span className="font-label text-[9px] text-[#B2A69E]">
                          {s.label}
                        </span>
                        <span className="font-display text-xl text-[#E68153]">
                          €{s.price}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="font-display text-2xl text-[#E68153] whitespace-nowrap"
                    data-testid={`menu-price-${m.id}`}
                  >
                    €{m.price}
                  </div>
                )}
              </div>

              <p className="mt-4 text-[#B2A69E] text-sm leading-relaxed">
                {m.description}
              </p>

              <ul className="mt-5 space-y-1.5 text-sm text-[#F5F1E6]/85">
                {m.items.map((it) => (
                  <li key={it} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C86C3F]" />
                    {it}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={open}
                  className="btn-copper px-4 py-2.5 rounded-full font-label text-[10px] flex items-center gap-2"
                  data-testid={`menu-order-${m.id}`}
                >
                  Ordina <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
                <a
                  href={`tel:${BUSINESS.phoneLink}`}
                  className="font-label text-[10px] text-[#B2A69E] hover:text-[#F5F1E6] transition-colors"
                >
                  oppure chiama
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ---------- QUALITY ---------- */
const QualitySection = () => (
  <section
    id="qualita"
    data-testid="quality-section"
    className="relative py-24 sm:py-32 bg-[#0F0F0F]"
  >
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1633712431553-02f1ff3e2548?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx3b29kJTIwdGV4dHVyZSUyMGRhcmt8ZW58MHx8fGJsYWNrfDE3Nzc3MTcyNTF8MA&ixlib=rb-4.1.0&q=85)",
        backgroundSize: "cover",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5">
          <SectionLabel testid="quality-eyebrow">
            La nostra ossessione
          </SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F1E6] mt-5 leading-[1.05]">
            Materia prima.
            <br />
            Tempo. Fuoco.
          </h2>
          <p className="mt-6 text-[#B2A69E] text-base sm:text-lg leading-relaxed max-w-md">
            Rosto's è un laboratorio: un pollo, fatto bene. Lo selezioniamo,
            lo mariniamo 24 ore, lo cuociamo lento finché non diventa il pollo
            arrosto che ricordi da bambino — solo migliore.
          </p>
          <div className="divider-ember mt-10" />
          <p className="mt-6 font-label text-[11px] text-[#C86C3F]">
            Filiera italiana · 0 km dove possibile · Senza antibiotici inutili
          </p>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUALITY.map((q) => {
            const Icon = ICONS[q.icon] || Flame;
            return (
              <div
                key={q.title}
                data-testid={`quality-pillar-${q.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="card-rosto rounded-2xl p-7"
              >
                <div className="w-11 h-11 rounded-full bg-[#251D1A] border border-[#362A26] flex items-center justify-center text-[#E68153]">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-[#F5F1E6] mt-5">
                  {q.title}
                </h3>
                <p className="mt-2 text-[#B2A69E] text-sm leading-relaxed">
                  {q.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- ORDER ---------- */
const OrderSection = () => (
  <section
    id="ordina"
    data-testid="order-section"
    className="py-24 sm:py-32 bg-[#1A1311]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <SectionLabel testid="order-eyebrow">Come ordinare</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F1E6] mt-5 leading-[1.05]">
          Caldo, croccante,
          <br />
          a casa tua.
        </h2>
        <p className="mt-5 text-[#B2A69E] text-base sm:text-lg">
          Scegli come preferisci. Pronto in 15 minuti per il ritiro, 30 per la
          consegna.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {ORDER_OPTIONS.map((opt) => {
          const isExt = opt.href.startsWith("http");
          return (
            <a
              key={opt.id}
              href={opt.href}
              target={isExt ? "_blank" : undefined}
              rel={isExt ? "noreferrer" : undefined}
              data-testid={`order-option-${opt.id}`}
              className="card-rosto rounded-2xl p-7 group flex flex-col"
            >
              <div className="flex items-start justify-between">
                <span className="font-label text-[10px] text-[#C86C3F]">
                  {opt.id === "phone"
                    ? "Diretto"
                    : opt.id === "pickup"
                      ? "In negozio"
                      : "Delivery"}
                </span>
                <ArrowUpRight className="w-5 h-5 text-[#B2A69E] group-hover:text-[#E68153] transition-colors" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-[#F5F1E6] mt-6">
                {opt.title}
              </h3>
              <p className="mt-2 text-sm text-[#B2A69E]">{opt.description}</p>
              <span className="mt-8 font-label text-[10px] text-[#E68153] group-hover:text-[#C86C3F] transition-colors">
                {opt.cta} →
              </span>
            </a>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- VISIT / CONTACT ---------- */
const VisitSection = () => (
  <section
    id="visita"
    data-testid="visit-section"
    className="py-24 sm:py-32 bg-[#0F0F0F]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* INFO */}
        <div className="lg:col-span-5 flex flex-col">
          <SectionLabel testid="visit-eyebrow">Dove siamo</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl text-[#F5F1E6] mt-5 leading-[1.05]">
            ROSTO'S
            <br />
            <span className="text-[#E68153]">Garbatella, Roma.</span>
          </h2>
          <p className="mt-4 text-[#B2A69E] text-sm sm:text-base">
            Asporto e delivery disponibili a Roma — Garbatella, Ostiense,
            Testaccio, San Paolo.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4" data-testid="visit-address">
              <span className="mt-1 w-9 h-9 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0">
                <MapPin className="w-4 h-4" />
              </span>
              <div>
                <p className="font-label text-[10px] text-[#C86C3F]">
                  Indirizzo
                </p>
                <p className="text-[#F5F1E6] mt-1">{BUSINESS.address}</p>
                <p className="text-[#B2A69E] text-sm">{BUSINESS.city}</p>
                <a
                  href={BUSINESS.links.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="visit-maps-link"
                  className="inline-flex items-center gap-1 mt-2 font-label text-[10px] text-[#E68153] hover:text-[#C86C3F]"
                >
                  Apri in Google Maps <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="visit-hours">
              <span className="mt-1 w-9 h-9 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0">
                <Clock className="w-4 h-4" />
              </span>
              <div>
                <p className="font-label text-[10px] text-[#C86C3F]">Orari</p>
                {BUSINESS.hours.map((h) => (
                  <p key={h.days} className="text-[#F5F1E6] mt-1">
                    {h.days}{" "}
                    <span className="text-[#B2A69E]">— {h.time}</span>
                  </p>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="visit-phone">
              <span className="mt-1 w-9 h-9 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0">
                <Phone className="w-4 h-4" />
              </span>
              <div>
                <p className="font-label text-[10px] text-[#C86C3F]">
                  Telefono
                </p>
                <a
                  href={`tel:${BUSINESS.phoneLink}`}
                  className="text-[#F5F1E6] mt-1 inline-block hover:text-[#E68153] transition-colors"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </div>
            </div>
          </div>

          {/* SECONDARY CTA BUTTONS */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="visit-ctas">
            <a
              href={BUSINESS.links.mapsLink}
              target="_blank"
              rel="noreferrer"
              data-testid="visit-cta-maps"
              className="btn-outline-copper px-5 py-3 rounded-full font-label text-[11px] flex items-center gap-2"
            >
              <MapPin className="w-3.5 h-3.5" />
              Indicazioni
            </a>
            <a
              href="/menu"
              data-testid="visit-cta-menu"
              className="btn-outline-copper px-5 py-3 rounded-full font-label text-[11px] flex items-center gap-2"
            >
              <Utensils className="w-3.5 h-3.5" />
              Guarda il menu
            </a>
          </div>

          {/* QR */}
          <a
            href="/menu"
            className="mt-12 card-rosto rounded-2xl p-6 flex items-center gap-5 group hover:border-[#C86C3F] transition-colors"
            data-testid="qr-menu-card"
          >
            <div className="bg-[#F5F1E6] rounded-xl p-3 flex-shrink-0">
              <QRCode
                value={`https://${typeof window !== "undefined" ? window.location.host : "rostos.it"}/menu`}
                size={96}
                bgColor="#F5F1E6"
                fgColor="#0F0F0F"
                level="M"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <QrCode className="w-4 h-4 text-[#E68153]" />
                <p className="font-label text-[10px] text-[#C86C3F]">
                  Menu QR
                </p>
              </div>
              <p className="font-display text-xl text-[#F5F1E6] mt-1 group-hover:text-[#E68153] transition-colors">
                Apri il menu
              </p>
              <p className="text-[#B2A69E] text-xs mt-1 max-w-[16rem]">
                Inquadra il QR oppure tocca qui per aprire il menu completo.
              </p>
            </div>
          </a>
        </div>

        {/* MAP */}
        <div className="lg:col-span-7">
          <div className="card-rosto rounded-2xl overflow-hidden h-80 lg:h-full min-h-[28rem] relative">
            <iframe
              title="Mappa Rosto's Garbatella"
              data-testid="visit-map"
              src={BUSINESS.links.mapsEmbed}
              className="w-full h-full border-0 grayscale-[40%] contrast-110"
              loading="lazy"
              allowFullScreen=""
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#362A26]" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="border-t border-[#362A26] bg-[#0F0F0F] py-12"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
      <div>
        <div className="flex items-center gap-4">
          <img
            src="/rostos-logo.png"
            alt="Rosto's"
            className="h-20 w-20 object-contain"
          />
          <span className="font-display text-4xl sm:text-5xl text-[#D9531E] leading-none uppercase">
            ROSTO'S
          </span>
        </div>
        <p className="mt-5 text-[#B2A69E] text-sm max-w-md">
          {BUSINESS.tagline}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-6 sm:items-center text-sm text-[#B2A69E]">
        <div className="flex items-center gap-2">
          <Utensils className="w-4 h-4 text-[#C86C3F]" />
          <span>{BUSINESS.hoursShort}</span>
        </div>
        <a
          href={`tel:${BUSINESS.phoneLink}`}
          className="hover:text-[#F5F1E6] transition-colors"
        >
          {BUSINESS.phoneDisplay}
        </a>
        <div className="flex items-center gap-2" data-testid="footer-socials">
          <a
            href={BUSINESS.links.whatsapp}
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp"
            data-testid="footer-whatsapp"
            className="w-9 h-9 rounded-full border border-[#362A26] flex items-center justify-center hover:text-[#E68153] hover:border-[#C86C3F] transition-colors"
          >
            <WhatsAppIcon className="w-4 h-4" />
          </a>
          <a
            href={BUSINESS.links.instagram}
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram"
            data-testid="footer-instagram"
            className="w-9 h-9 rounded-full border border-[#362A26] flex items-center justify-center hover:text-[#E68153] hover:border-[#C86C3F] transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <a
            href={BUSINESS.links.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            data-testid="footer-facebook"
            className="w-9 h-9 rounded-full border border-[#362A26] flex items-center justify-center hover:text-[#E68153] hover:border-[#C86C3F] transition-colors"
          >
            <Facebook className="w-4 h-4" />
          </a>
          <a
            href={BUSINESS.links.tiktok}
            target="_blank"
            rel="noreferrer"
            aria-label="TikTok"
            data-testid="footer-tiktok"
            className="w-9 h-9 rounded-full border border-[#362A26] flex items-center justify-center hover:text-[#E68153] hover:border-[#C86C3F] transition-colors"
          >
            <TikTokIcon className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-[#362A26] flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#B2A69E]/70">
      <p>© {new Date().getFullYear()} Rosto's — Tutti i diritti riservati.</p>
      <p>
        Fatto con fuoco e pazienza in Garbatella.
      </p>
    </div>
  </footer>
);

/* ---------- STICKY MOBILE CTA ---------- */
const MobileCta = () => {
  const [show, setShow] = useState(false);
  const { open } = useOrderModal();
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 480);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="mobile-sticky-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pt-2 transition-transform duration-300 ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="backdrop-blur-xl bg-[#0F0F0F]/90 border border-[#362A26] rounded-full p-1.5 flex items-center gap-1 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.6)]">
        <button
          type="button"
          onClick={open}
          className="flex-1 btn-copper rounded-full py-3 font-label text-[11px] flex items-center justify-center gap-2"
          data-testid="mobile-cta-order"
        >
          <Bike className="w-4 h-4" />
          Ordina a domicilio
        </button>
        <a
          href={`tel:${BUSINESS.phoneLink}`}
          className="w-12 h-12 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153]"
          data-testid="mobile-cta-call"
          aria-label="Chiama"
        >
          <Phone className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

/* ---------- PAGE ---------- */
/* ---------- FAQ (SEO) ---------- */
const FAQ_ITEMS = [
  {
    q: "Qual è il miglior pollo arrosto a Roma?",
    a: "Per molti dei nostri clienti il pollo arrosto di ROSTO'S a Garbatella è tra i migliori di Roma. Usiamo pollo Campese italiano allevato all'aperto, marinato 48 ore con olio EVO ed erbe mediterranee, cotto lentamente al forno: pelle croccante, carne succosa. Il giudizio, comunque, lo lasciamo a te.",
  },
  {
    q: "Dove posso mangiare un buon pollo arrosto a Garbatella?",
    a: "Da ROSTO'S, in Via Giuseppe Candeo 16, 00154 Roma — Garbatella. Da Martedì a Domenica 12:00–15:00 e 18:00–22:00. Lunedì solo cena 18:00–22:00. Asporto e delivery a domicilio in tutta Roma sud.",
  },
  {
    q: "Come posso ordinare il pollo arrosto a domicilio a Roma?",
    a: "Puoi ordinare il pollo arrosto di ROSTO'S a domicilio tramite Glovo, Just Eat o Deliveroo, oppure chiamare il +39 392 35 55 502 per il ritiro in negozio a Garbatella.",
  },
  {
    q: "Che tipo di pollo usate per il pollo arrosto?",
    a: "Usiamo solo pollo Campese italiano: una razza rustica allevata all'aperto, con tempi di crescita lenti, carne soda e saporita. Lo mariniamo 48 ore con olio EVO, agrumi ed erbe mediterranee.",
  },
  {
    q: "Quanto costa un pollo arrosto da ROSTO'S?",
    a: "Un quarto di pollo Campese costa €4,90, mezzo pollo €8,90, pollo intero €14,90. Le combo con patate al forno partono da €9,90 (quarto + patate regular) fino a €19,90 (pollo intero + patate large).",
  },
  {
    q: "Fate consegna a domicilio anche fuori Garbatella?",
    a: "Sì, consegniamo a domicilio in tutta Roma sud — Garbatella, Ostiense, Testaccio, San Paolo e zone limitrofe — tramite Glovo, Just Eat e Deliveroo.",
  },
];

const FaqSection = () => (
  <section
    data-testid="faq-section"
    className="py-20 sm:py-28 bg-[#1A1311] border-t border-[#362A26]"
  >
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionLabel testid="faq-eyebrow">Domande frequenti</SectionLabel>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F1E6] mt-5 leading-[1.1]">
        Quello che ci chiedono sul pollo arrosto a Roma.
      </h2>
      <div className="mt-10 divide-y divide-[#362A26] border-y border-[#362A26]">
        {FAQ_ITEMS.map((f, i) => (
          <details
            key={i}
            data-testid={`faq-item-${i}`}
            className="group py-5"
          >
            <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
              <h3 className="font-display text-lg sm:text-xl text-[#F5F1E6] group-hover:text-[#E68153] transition-colors">
                {f.q}
              </h3>
              <span className="w-8 h-8 rounded-full border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0 group-open:rotate-45 transition-transform">
                <ChevronDown className="w-4 h-4 -rotate-90 group-open:rotate-0 transition-transform" />
              </span>
            </summary>
            <p className="mt-4 text-[#B2A69E] leading-relaxed text-sm sm:text-base">
              {f.a}
            </p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export default function RostoLanding() {
  return (
    <main data-testid="rosto-landing" className="bg-[#0F0F0F] text-[#F5F1E6]">
      <Nav />
      <Hero />
      <Strip />
      <AboutSeo />
      <MenuSection />
      <QualitySection />
      <OrderSection />
      <VisitSection />
      <FaqSection />
      <Footer />
      <MobileCta />
    </main>
  );
}
